import { columnAppliesToProductsOnly, columnCalcFromMetadata } from "./cogsColumnEdit";
import { columnAppliesRecipeIds } from "./cogsColumnProductScope";
import { shelfVariance, type ShelfVariance } from "./cogsShelfVariance";
import { planColumnFormulaWrites } from "./haulRateEvaluate";
import {
  bandRoundUpFromColumns,
  evaluatePricingLayers,
  type PricingLayerColumnDef,
} from "./pricingLayerEvaluate";
import type { MfgPricingLayerBand, MfgPricingLayerColumn, MfgPricingLayerGridRow } from "./cogsTypes";

export type UnattachedColumnPreview = {
  column_id: string;
  label: string;
  band: MfgPricingLayerBand;
  amount_usd: number | null;
  note: string;
  wholesale: ShelfVariance;
  msrp: ShelfVariance;
  wholesale_total_usd: number;
  msrp_total_usd: number;
};

function columnDef(column: MfgPricingLayerColumn, active: boolean): PricingLayerColumnDef {
  return {
    id: column.id,
    band: column.band,
    key: column.key,
    label: column.label,
    op: column.op,
    source_kind: column.source_kind,
    source_column_id: column.source_column_id,
    source_band: column.source_band,
    sort_order: column.sort_order,
    is_active: active,
    ...(column.metadata?.rate_kind === "markup" ? { rate_kind: "markup" as const } : {}),
  };
}

function formulaAmount(row: MfgPricingLayerGridRow, column: MfgPricingLayerColumn): number | null | "missing-size" {
  const calc = columnCalcFromMetadata(column.metadata);
  if (!calc) return null;
  const planned = planColumnFormulaWrites(
    [
      {
        recipe_id: row.subject_id,
        kind: row.recipe_kind || "product",
        recipe_code: row.code,
        name: row.name,
        recipe_group_id: null,
        size_label: row.size_band,
      },
    ],
    calc.basis,
    calc.rate_usd,
    columnAppliesToProductsOnly(column),
    columnAppliesRecipeIds(column.metadata),
  );
  if (planned.clear_recipe_ids.includes(row.subject_id)) return "missing-size";
  const write = planned.writes.find((item) => item.recipe_id === row.subject_id);
  return write ? write.amount_usd : null;
}

function amountFor(row: MfgPricingLayerGridRow, column: MfgPricingLayerColumn): {
  amount_usd: number | null;
  note: string;
} {
  const stored = row.values_by_column_id[column.id];
  if (typeof stored === "number" && Number.isFinite(stored)) {
    return { amount_usd: stored, note: "Saved amount. This column is not on the grid." };
  }
  const formula = formulaAmount(row, column);
  if (formula === "missing-size") {
    return { amount_usd: null, note: "Needs a size before this formula can price it." };
  }
  if (typeof formula === "number") {
    return { amount_usd: formula, note: "From the column formula. This column is not on the grid." };
  }
  return { amount_usd: null, note: "No amount yet." };
}

/** What each inactive column would do to this product if it were turned on. The grid totals stay as they are. */
export function unattachedColumnPreviews(
  row: MfgPricingLayerGridRow,
  columns: readonly MfgPricingLayerColumn[],
): UnattachedColumnPreview[] {
  const inactive = columns.filter((column) => !column.is_active);
  return inactive.map((column) => {
    const priced = amountFor(row, column);
    if (priced.amount_usd == null) {
      return {
        column_id: column.id,
        label: column.label,
        band: column.band,
        amount_usd: null,
        note: priced.note,
        wholesale: shelfVariance(row.wholesale_total_usd, row.shelf_wholesale_usd),
        msrp: shelfVariance(row.msrp_total_usd, row.shelf_msrp_usd),
        wholesale_total_usd: row.wholesale_total_usd,
        msrp_total_usd: row.msrp_total_usd,
      };
    }
    const evaluated = evaluatePricingLayers({
      materials_cost_usd: row.materials_cost_usd ?? 0,
      columns: columns.map((item) => columnDef(item, item.is_active || item.id === column.id)),
      values_by_column_id: { ...row.values_by_column_id, [column.id]: priced.amount_usd },
      round_up: bandRoundUpFromColumns(columns),
    });
    return {
      column_id: column.id,
      label: column.label,
      band: column.band,
      amount_usd: priced.amount_usd,
      note: priced.note,
      wholesale: shelfVariance(evaluated.wholesale_total_usd, row.shelf_wholesale_usd),
      msrp: shelfVariance(evaluated.msrp_total_usd, row.shelf_msrp_usd),
      wholesale_total_usd: evaluated.wholesale_total_usd,
      msrp_total_usd: evaluated.msrp_total_usd,
    };
  });
}
